/**
 * The auth contract the router pipeline relies on.
 *
 * A leaf module with no imports of its own: `authUser` is a runtime import for guards,
 * manifests and AuthContext, and reaching it through `router.tsx` (which builds the route
 * tree) or the `@hub/router` barrel would close a module-init cycle.
 */

/** Minimum user shape the router kernel reads. `AuthState` widens it to the full BFF shape. */
export type RouterAuthUser = { flags: string[] | null };

/**
 * The four reachable session states, as a union so the other twelve boolean combinations
 * cannot be constructed and no consumer has to re-derive which one means what. WWP-604.
 */
export type RouterAuthState<TUser extends RouterAuthUser = RouterAuthUser> =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; user: TUser }
  // Probe failed without a verdict on the session (5xx, unclassified error): auth is
  // indeterminate, so guards route to the error page rather than to login.
  | { status: 'unresolved' };

/**
 * The states the router is allowed to see. `loading` is absent by construction: the
 * bootstrap gate resolves it before mounting the router, so no guard has to answer a
 * state it cannot decide, and none can mistake it for anonymous.
 *
 * `Extract`, not `Exclude`: a status added to `RouterAuthState` does not silently become
 * router-visible. The gate then fails to compile until someone decides whether the router
 * may see it.
 */
export type SettledRouterAuth<TUser extends RouterAuthUser = RouterAuthUser> = Extract<
  RouterAuthState<TUser>,
  { status: 'anonymous' | 'authenticated' | 'unresolved' }
>;

/** The user when authenticated, `null` in every other state. */
export function authUser<TUser extends RouterAuthUser>(auth: RouterAuthState<TUser>): TUser | null {
  return auth.status === 'authenticated' ? auth.user : null;
}

/**
 * Call from the `default` branch of a switch that decides per state. Adding a status then
 * fails to compile there instead of silently taking whichever branch comes last.
 */
export function unhandledAuthStatus(auth: never): never {
  throw new Error(`Unhandled auth status: ${(auth as RouterAuthState).status}`);
}
